import { useCallback, useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { changeTariffFromBackend } from '@entities/tariff/lib';
import { showToast } from '@features';
import {
    useDeleteChecklist,
    useGetChecklistData,
    useSwitchChecklistItem,
    useUpdateChecklistData,
} from '@features/Checklist/api/hooks';
import { getChecklistToastScope } from '@features/Checklist/lib/helpers';
import { useChecklistDraft, useChecklistViewState } from '@features/Checklist/model/hooks/index';
import { IChecklistTaskDto } from '@features/Checklist/model/types';
import { keysToCamel } from '@shared/helpers';
import { IDataResponse } from '@shared/model/types/dataResponse.inteface';

import { useChecklistClipboard } from './useChecklistClipboard';

export const useChecklistController = (positionGuid: string) => {
    const toastScope = getChecklistToastScope(positionGuid);
    const hasAutoOpenedChecklistRef = useRef(false);
    const { t } = useTranslation();
    const {
        data: checklistData,
        isPending: isPendingData,
        isError: isChecklistDataError,
    } = useGetChecklistData(positionGuid);
    const {
        isOpenChecklist,
        isCopied,
        isEditMode,
        isOpenDeletePopup,
        handleClick,
        markCopied,
        openDeletePopup,
        closeDeletePopup,
        openChecklist,
        closeChecklist,
        enableEditMode,
        disableEditMode: disableViewEditMode,
        resetAfterDelete,
    } = useChecklistViewState();

    const {
        rawChecklist,
        persistedChecklist,
        isChecklistHydrated,
        disableToUpdate,
        lastAddedChecklistItemClientId,
        appendChecklistItems,
        resetDraftToPersisted,
        resetChecklist,
        restoreRawChecklist,
        applyPersistedChecklist,
        prepareUpdatePayload,
        addChecklistItem,
        changeChecklistItemText,
        toggleChecklistItem,
        deleteChecklistItem,
        moveChecklistItemToBottom,
        reorderChecklistItem,
    } = useChecklistDraft({
        checklistData: checklistData?.data,
        isEditMode,
    });

    const hasChecklist = rawChecklist.length > 0;

    useEffect(() => {
        if (!hasChecklist || hasAutoOpenedChecklistRef.current) {
            return;
        }

        hasAutoOpenedChecklistRef.current = true;
        openChecklist();
    }, [hasChecklist, openChecklist]);

    const updateChecklistDataMutation = useUpdateChecklistData();
    const deleteChecklistMutation = useDeleteChecklist(() => {
        resetAfterDelete();
        resetChecklist();
    });
    const switchChecklistMutation = useSwitchChecklistItem();

    const hideTriggerBtn =
        checklistData?.status === 'error' && !checklistData?.context?.canViewChecklistBtn;

    const handleDelete = useCallback(() => {
        deleteChecklistMutation.mutate({ positionGuid });
    }, [deleteChecklistMutation, positionGuid]);

    const handleChecklistTriggerClick = useCallback(async () => {
        const isNotAvailableByTariff = checklistData?.status === 'tariff-error';
        const shouldStartEditingEmptyChecklist = !isOpenChecklist && rawChecklist.length === 0;
        if (isNotAvailableByTariff) {
            await changeTariffFromBackend(checklistData?.tariffPopupUrl);
            return;
        }

        if (shouldStartEditingEmptyChecklist) {
            openChecklist();
            if (checklistData?.context?.canModifyChecklist) {
                enableEditMode();
                addChecklistItem();
            }
            return;
        }

        handleClick();
    }, [
        checklistData,
        addChecklistItem,
        enableEditMode,
        handleClick,
        isOpenChecklist,
        openChecklist,
        rawChecklist.length,
    ]);

    const { handleCopy, handlePasteButtonClick } = useChecklistClipboard({
        checklist: rawChecklist,
        positionGuid,
        isEditMode,
        markCopied,
        appendChecklistItems,
        moveChecklistItemToBottom,
    });

    const onToggleChecklistItem = useCallback(
        (clientId: string) => {
            const prevChecklist = rawChecklist;
            const toggledChecklistItemId = toggleChecklistItem(clientId);

            if (toggledChecklistItemId === undefined) {
                return;
            }

            switchChecklistMutation.mutate(
                {
                    positionGuid,
                    checklistItemId: String(toggledChecklistItemId),
                },
                {
                    onError: () => {
                        restoreRawChecklist(prevChecklist);
                    },
                }
            );
        },
        [
            positionGuid,
            rawChecklist,
            restoreRawChecklist,
            switchChecklistMutation,
            toggleChecklistItem,
        ]
    );

    const handleUpdateChecklistData = useCallback(async () => {
        const response = await updateChecklistDataMutation.mutateAsync({
            positionGuid,
            data: prepareUpdatePayload(),
        });
        const parsedData = keysToCamel<IDataResponse<IChecklistTaskDto[]>>(response.data);

        if (parsedData.data) {
            applyPersistedChecklist(parsedData.data);
        }

        disableViewEditMode();
        showToast(t('Checklist.UpdateChecklist.Success.text'), {
            scope: toastScope,
            type: 'success',
        });

        return response;
    }, [
        t,
        applyPersistedChecklist,
        disableViewEditMode,
        positionGuid,
        prepareUpdatePayload,
        toastScope,
        updateChecklistDataMutation,
    ]);

    const handleDisableEditMode = useCallback(() => {
        disableViewEditMode();
        resetDraftToPersisted();
        console.log('persistedChecklist', persistedChecklist);
        if (persistedChecklist.length === 0) closeChecklist();
    }, [persistedChecklist, disableViewEditMode, resetDraftToPersisted, closeChecklist]);

    return {
        rawChecklist,
        persistedChecklist,
        isChecklistHydrated,
        canModifyChecklist: checklistData?.context?.canModifyChecklist,
        disableToUpdate,
        isOpenChecklist,
        isCopied,
        isEditMode,
        isOpenDeletePopup,
        isUpdateChecklistPending: updateChecklistDataMutation.isPending,
        isPendingData,
        isChecklistDataError,
        hideTriggerBtn,
        lastAddedChecklistItemClientId,
        handleClick: handleChecklistTriggerClick,
        handleCopy,
        handlePasteButtonClick,
        handleDelete,
        handleUpdateChecklistData,
        openDeletePopup,
        closeDeletePopup,
        enableEditMode,
        disableEditMode: handleDisableEditMode,
        onDeleteChecklistItem: deleteChecklistItem,
        onAddChecklistItem: addChecklistItem,
        onReorderChecklistItem: reorderChecklistItem,
        onChangeChecklistItemText: changeChecklistItemText,
        onToggleChecklistItem,
    };
};
